import { push } from 'connected-react-router';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { ordersUrl } from 'src/app/client/api';
import { createDeliveryAddress, updateDeliveryAddress } from 'src/app/client/models/deliveryAddress';
import {
  createOrder,
  createOrderItem,
  fetchOrder, // deprecated
  getCurrentUserOrderHistory, // deprecated
  getOrderDeliveryWindows,
  getOrderHistory,
  processOrder,
  updateOrder,
} from 'src/app/client/models/order';
import ConsumerWebPropTypes from 'src/app/propTypes';
import { orderTracking } from 'src/app/router/paths';
import { cartClear, checkoutSetOrder, clearResource, segmentTrack } from 'src/app/state/actions';
import * as actionTypes from 'src/app/state/actions/types';
import { getCartItems, getCurrentAccountId, getCurrentOrderId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import { extractErrorCodes } from 'src/app/utils';

export function createOrdersAndOrderItems() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const accountId = getCurrentAccountId(state);
    const cartItems = getCartItems(state);

    const { data: responseData } = await createOrder(client, { accountId });
    const orderId = responseData.data.id;

    // this must be synchronous otherwise there's a race condition on the back-end
    // for subtotal computation
    for (let i = 0; i < cartItems.length; i++) {
      const cartItem = cartItems[i];

      const { fulfillmentId, productId, price } = cartItem;

      await createOrderItem(client, {
        orderId,
        fulfillmentId,
        productId,
        price,
      });
    }

    await dispatch(checkoutSetOrder(orderId));

    return orderId;
  };
}

// TODO: Deprecated, remove when bff order history is in full use.
export function loadCurrentUserOrderHistory() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch({
      type: actionTypes.FETCH_ORDERS,
    });
    const state = getState();
    const { client } = state;
    const orders = await getCurrentUserOrderHistory(client);
    dispatch({
      type: actionTypes.FETCH_ORDERS_SUCCESS,
    });
    return orders;
  };
}

export function setOrderDeliveryWindow(deliveryWindowId) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;

    const orderId = getCurrentOrderId(state);
    const accountId = getCurrentAccountId(state);

    await updateOrder(client, {
      orderId,
      accountId,
      relationships: {
        deliveryWindowId,
      },
    });
  };
}

export function loadAvailableDeliveryWindows() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const orderId = getCurrentOrderId(state);

    await dispatch(clearResource(ConsumerWebPropTypes.deliveryWindow));
    await getOrderDeliveryWindows(client, { orderId });
  };
}

export function accountCreateAddress(deliveryAddressAttributes) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);
    const response = await createDeliveryAddress(client, { accountId, attributes: deliveryAddressAttributes });
    return response;
  };
}

export function accountUpdateAddressAction(addressId, deliveryAddressAttributes) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);
    await updateDeliveryAddress(client, addressId, {
      accountId,
      attributes: deliveryAddressAttributes,
    });
  };
}

export function createOrderDeliveryAddress(deliveryAddressAttributes) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const orderId = getCurrentOrderId(state);
    const accountId = getCurrentAccountId(state);

    const response = await createDeliveryAddress(client, { accountId, attributes: deliveryAddressAttributes });
    const deliveryAddressId = response.data.data.id;

    await updateOrder(client, { orderId, accountId, relationships: { deliveryAddressId } });

    await dispatch(setOrderDeliveryAddress(deliveryAddressId));
  };
}

export function setOrderDeliveryAddress(deliveryAddressId) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;

    const orderId = getCurrentOrderId(state);
    const accountId = getCurrentAccountId(state);

    await updateOrder(client, {
      orderId,
      accountId,
      relationships: {
        deliveryAddressId,
      },
    });
  };
}

export function setOrderPaymentCard(paymentCardId) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;

    const orderId = getCurrentOrderId(state);
    const accountId = getCurrentAccountId(state);

    await updateOrder(client, {
      orderId,
      accountId,
      attributes: {
        stripe_card_id: paymentCardId,
      },
    });
  };
}

export function completeOrder() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const orderId = getCurrentOrderId(state);

    await processOrder(client, { orderId });

    await dispatch(cartClear());
  };
}

export function loadOrder(orderId) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;

    await fetchOrder(client, { orderId });
  };
}

export function fetchOrderHistory() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({ type: actionTypes.FETCH_ORDER_HISTORY });
    try {
      const bffOrders = await getOrderHistory(client);

      dispatch({
        type: actionTypes.FETCH_ORDER_HISTORY_SUCCESS,
        data: bffOrders,
      });
    } catch (error) {
      const bffError = error?.error ?? '';
      const api = ordersUrl();
      const trackErrorPayload = {
        errorMessage: 'Fetch Order History Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(error),
        source: window.location.href,
      };

      segmentTrack(UNIFIED.error, trackErrorPayload);

      dispatch({
        type: actionTypes.FETCH_ORDER_HISTORY_FAILURE,
        error: error,
      });
    }
  };
}

export function redirectToOrderTracking(orderId: string) {
  return async (dispatch: Dispatch) => {
    dispatch(push(orderTracking.toPath({ orderId })));
  };
}
